import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import {
  signIn as amplifySignIn,
  confirmSignIn as amplifyConfirmSignIn,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import type { User, UserRole } from '@/types';
import { usersApi } from '@/api';

// TODO: Fill these from environment variables
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID as string,
      loginWith: {
        oauth: {
          domain: import.meta.env.VITE_COGNITO_DOMAIN as string,
          scopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
          redirectSignIn: [window.location.origin + '/dashboard'],
          redirectSignOut: [window.location.origin + '/login'],
          responseType: 'code',
        },
      },
    },
  },
});

export interface AuthContextValue {
  currentUser: User | null;       // effective user (role overridden when impersonating)
  realUser: User | null;          // always the actual logged-in user
  isLoading: boolean;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  newPasswordRequired: boolean;
  pendingEmail: string;
  signIn: (username: string, password: string) => Promise<void>;
  confirmNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  hasRole: (roles: UserRole[]) => boolean;
  impersonateRole: (role: UserRole | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SESSION_KEY = 'repairapp_impersonate_role';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [realUser, setRealUser] = useState<User | null>(null);
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [impersonatedRole, setImpersonatedRole] = useState<UserRole | null>(() => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    return stored as UserRole | null;
  });
  const [isLoading, setIsLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      await getCurrentUser();
      const attrs = await fetchUserAttributes();

      // Build a lightweight user from Cognito attributes while we fetch the full profile
      const cognitoUser: User = {
        id: 0, // temporary placeholder — replaced after sync with the DB row id
        email: attrs.email ?? '',
        name: attrs.name ?? attrs.email ?? '',
        role: (attrs['custom:role'] as UserRole) ?? 'submitter',
        company: attrs['custom:company'] ?? null,
        active: true,
        cognitoSub: attrs.sub ?? '',
        createdAt: new Date().toISOString(),
      };
      setRealUser(cognitoUser);

      // Sync user record with backend (creates/updates DB row)
      try {
        const synced = await usersApi.sync();
        setRealUser(synced);
      } catch {
        // Non-fatal: use Cognito attributes as fallback
      }
    } catch {
      setRealUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check for an existing session on mount
    loadUser();

    // Re-run loadUser when Amplify completes an OAuth redirect sign-in,
    // since the code exchange happens asynchronously after the page loads.
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        loadUser();
      }
    });

    return unsubscribe;
  }, [loadUser]);

  const signIn = useCallback(
    async (username: string, password: string) => {
      const result = await amplifySignIn({ username, password });
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNewPasswordRequired(true);
        setPendingEmail(username);
        return;
      }
      setNewPasswordRequired(false);
      await loadUser();
    },
    [loadUser],
  );

  const confirmNewPassword = useCallback(
    async (newPassword: string) => {
      await amplifyConfirmSignIn({ challengeResponse: newPassword });
      setNewPasswordRequired(false);
      setPendingEmail('');
      await loadUser();
    },
    [loadUser],
  );

  const signOut = useCallback(async () => {
    await amplifySignOut();
    setRealUser(null);
    setImpersonatedRole(null);
    sessionStorage.removeItem(SESSION_KEY);
  }, []);

  // Effective user — role overridden when impersonating (only allowed for super_admin)
  const currentUser: User | null =
    realUser && impersonatedRole && realUser.role === 'super_admin'
      ? { ...realUser, role: impersonatedRole }
      : realUser;

  const isImpersonating = !!impersonatedRole && realUser?.role === 'super_admin';

  const hasRole = useCallback(
    (roles: UserRole[]) => {
      if (!currentUser) return false;
      return roles.includes(currentUser.role);
    },
    [currentUser],
  );

  const impersonateRole = useCallback((role: UserRole | null) => {
    setImpersonatedRole(role);
    if (role) {
      sessionStorage.setItem(SESSION_KEY, role);
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        realUser,
        isLoading,
        isAuthenticated: !!currentUser,
        isImpersonating,
        newPasswordRequired,
        pendingEmail,
        signIn,
        confirmNewPassword,
        signOut,
        hasRole,
        impersonateRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

// Re-export fetchAuthSession for use in axios interceptor
export { fetchAuthSession };
